import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { find } from 'lodash';
import { IntlProvider } from 'react-intl';

import { usePostMessageContext } from '../../post-message/PostMessage';
import { featureEnvironmentKeys, isFeatureEnvironment } from '../../util/featureFlagEnvironments';
import mergeTimeZones from './mergeTimeZones';
import { useLazyLocaleMessages } from './useLazyLocaleMessages';
import { useMergeMessages } from './useMergeMessages';
import { consolidateMessages } from './util/consolidateMessages';
import { mergeCdsDefaultMessages } from './util/mergeCdsDefaultMessages';

const PREVIEW_LANGUAGES = ['ro-RO', 'bg-BG'];

// TODO: Make configurable at platform and entity levels
const ALL_LANGUAGES = [
  'da',
  'de',
  'en-US',
  'en-GB',
  'es-CO',
  'fr-FR',
  'nl-NL',
  'no-NO',
  'sv-SE',
  'it-IT',
  'pl-PL',
  'pt',
  'ru-RU',
  'hr-HR',
  'lt-LT',
  'et-EE',
  'uk',
  'fi-FI',
  'ro-RO',
  'bg-BG',
];

// Outside preview environments, exclude PREVIEW_LANGUAGES (ro-RO, bg-BG) from the menu.
const getDefaultLanguages = () =>
  isFeatureEnvironment(featureEnvironmentKeys.preview)
    ? ALL_LANGUAGES
    : ALL_LANGUAGES.filter(lang => !PREVIEW_LANGUAGES.includes(lang));

const DEFAULT_LANGUAGE = 'en-US';

export const LocalizationContext = React.createContext();

export const useLocalizationContext = (defaultValue = null) => {
  const context = useContext(LocalizationContext);
  if (!context && !defaultValue) {
    throw new Error('useLocalizationContext cannot be called outside of the LocalizationContext context provider');
  }
  return context || defaultValue;
};

const cachedLanguageId = localStorage.getItem('language');

const browserLanguageId = navigator?.languages?.[0] || navigator?.language || DEFAULT_LANGUAGE;

const deriveLazySupportedLanguages = ({ messages, messagesLoaders, supportedLocales }) => {
  if (supportedLocales?.length) {
    return supportedLocales;
  }

  return [...new Set([...Object.keys(messages || {}), ...Object.keys(messagesLoaders || {})])];
};

const resolveLazyFallbackLocale = ({ supportedLanguages, messages, messagesLoaders, excludeLocales = [] }) => {
  const excluded = new Set(excludeLocales);
  const isLoadable = locale => !excluded.has(locale) && Boolean(messages?.[locale] || messagesLoaders?.[locale]);

  if (isLoadable(DEFAULT_LANGUAGE)) {
    return DEFAULT_LANGUAGE;
  }

  return supportedLanguages?.find?.(isLoadable) ?? null;
};

const resolveLazySeedLocale = ({ supportedLanguages }) =>
  (supportedLanguages?.includes?.(DEFAULT_LANGUAGE) && DEFAULT_LANGUAGE) || supportedLanguages?.[0] || DEFAULT_LANGUAGE;

export const Localization = ({
  initialLocale,
  children,
  messages: msgs,
  messagesLoaders,
  supportedLocales,
  isIframe,
  initialTimeZone = 'America/Chicago',
}) => {
  const isLazyMode = Boolean(messagesLoaders && Object.keys(messagesLoaders).length > 0);
  const [editMode, setEditMode] = useState(false);
  const [timeZone, setTimeZone] = useState(initialTimeZone);
  const [supportedLanguages, setSupportedLanguages] = useState(() => {
    if (isLazyMode) {
      return deriveLazySupportedLanguages({ messages: msgs, messagesLoaders, supportedLocales });
    }

    return getDefaultLanguages();
  });
  const { messages: appMessages } = useLocalizationContext({});
  const [parentAppMessages, setParentAppMessages] = useState({});
  const { sendMessage } = usePostMessageContext();
  const { localeMessages, loadingLocale, ensureLocaleLoaded, seedCdsOnlyLocale } = useLazyLocaleMessages({
    messages: msgs,
    messagesLoaders,
    isLazyMode,
  });
  const effectiveMessages = isLazyMode ? localeMessages : msgs;

  useEffect(() => {
    if (isLazyMode) {
      const newSupportedLanguages = deriveLazySupportedLanguages({ messages: msgs, messagesLoaders, supportedLocales });

      setSupportedLanguages(newSupportedLanguages);
    }
  }, [isLazyMode, messagesLoaders, msgs, supportedLocales]);

  const mergedAppMessages = useMemo(() => {
    const msgsWithCdsDefaults = isLazyMode ? effectiveMessages : mergeCdsDefaultMessages(effectiveMessages);
    const parentAppMessagesConslidated = consolidateMessages({ messages: appMessages || {} });
    const appMessagesConsolidated = consolidateMessages({ messages: msgsWithCdsDefaults });

    const mergedMessagesConsolidated = {};

    for (const locale in parentAppMessagesConslidated) {
      mergedMessagesConsolidated[locale] = {
        ...appMessagesConsolidated[locale],
        ...parentAppMessagesConslidated[locale],
      };
    }

    for (const locale in appMessagesConsolidated) {
      if (!mergedMessagesConsolidated[locale]) {
        mergedMessagesConsolidated[locale] = appMessagesConsolidated[locale];
      }
    }

    return mergedMessagesConsolidated;
  }, [appMessages, effectiveMessages, isLazyMode]);

  const messagesWithTimeZones = useMemo(() => mergeTimeZones(mergedAppMessages), [mergedAppMessages]);
  const mergedMessages = useMergeMessages(messagesWithTimeZones);

  const initialUserLocale = useMemo(() => {
    const supportedCachedLanguageId =
      (cachedLanguageId && find(supportedLanguages, l => l.toLowerCase() === cachedLanguageId.toLowerCase())) || null;

    const lazyFallback = resolveLazyFallbackLocale({
      supportedLanguages,
      messages: msgs,
      messagesLoaders,
    });
    const browserFallback = isLazyMode ? lazyFallback : DEFAULT_LANGUAGE;
    const supportedBrowserLanguageId =
      find(supportedLanguages, l => l.toLowerCase() === browserLanguageId.toLowerCase()) || browserFallback;

    return initialLocale || supportedCachedLanguageId || supportedBrowserLanguageId;
  }, [initialLocale, isLazyMode, messagesLoaders, msgs, supportedLanguages]);

  const [userLocale, _setUserLocale] = useState(() => (isLazyMode ? null : initialUserLocale));
  const intendedLocaleRef = useRef(initialUserLocale);
  const bootstrapLocaleRef = useRef(initialUserLocale);
  const userHasSelectedLocaleRef = useRef(false);
  const bootstrapCompletedRef = useRef(false);
  const bootstrappedInitialLocaleRef = useRef(null);

  const activeLocale = userLocale || DEFAULT_LANGUAGE;

  const consolidatedMessages = useMemo(() => {
    if (!mergedMessages) {
      return {};
    }

    return consolidateMessages({ messages: mergedMessages })?.[activeLocale] || {};
  }, [activeLocale, mergedMessages]);

  const setUserLocale = useCallback(
    async (locale, persist = true) => {
      if (!locale) {
        return;
      }

      if (isLazyMode) {
        intendedLocaleRef.current = locale;

        const loaded = await ensureLocaleLoaded(locale);

        if (!loaded) {
          if (intendedLocaleRef.current === locale) {
            intendedLocaleRef.current = bootstrapLocaleRef.current;

            if (bootstrapCompletedRef.current && localeMessages[bootstrapLocaleRef.current]) {
              _setUserLocale(bootstrapLocaleRef.current);
            }
          }

          return;
        }

        if (intendedLocaleRef.current !== locale) {
          return;
        }

        userHasSelectedLocaleRef.current = true;
      }

      if (persist) {
        localStorage.setItem('language', locale);
      }

      _setUserLocale(locale);
    },
    [ensureLocaleLoaded, isLazyMode, localeMessages]
  );

  useEffect(() => {
    if (!isLazyMode) {
      return;
    }

    const targetLocale = initialUserLocale;

    if (bootstrapCompletedRef.current && bootstrappedInitialLocaleRef.current === targetLocale) {
      return;
    }

    bootstrappedInitialLocaleRef.current = targetLocale;
    bootstrapLocaleRef.current = targetLocale;

    if (!userHasSelectedLocaleRef.current) {
      intendedLocaleRef.current = targetLocale;
    }

    (async () => {
      const loaded = await ensureLocaleLoaded(targetLocale);

      if (loaded) {
        if (intendedLocaleRef.current !== targetLocale) {
          bootstrapCompletedRef.current = true;

          return;
        }

        localStorage.setItem('language', targetLocale);
        _setUserLocale(targetLocale);
        bootstrapCompletedRef.current = true;

        return;
      }

      const failedLocales = [targetLocale];
      let fallbackLocale = resolveLazyFallbackLocale({
        supportedLanguages,
        messages: msgs,
        messagesLoaders,
        excludeLocales: failedLocales,
      });

      while (fallbackLocale) {
        console.warn(`Failed to load initial locale "${targetLocale}", falling back to "${fallbackLocale}"`);

        const fallbackLoaded = await ensureLocaleLoaded(fallbackLocale);

        if (fallbackLoaded && intendedLocaleRef.current === targetLocale && !userHasSelectedLocaleRef.current) {
          bootstrapLocaleRef.current = fallbackLocale;
          intendedLocaleRef.current = fallbackLocale;
          localStorage.setItem('language', fallbackLocale);
          _setUserLocale(fallbackLocale);
          bootstrapCompletedRef.current = true;

          return;
        }

        failedLocales.push(fallbackLocale);
        fallbackLocale = resolveLazyFallbackLocale({
          supportedLanguages,
          messages: msgs,
          messagesLoaders,
          excludeLocales: failedLocales,
        });
      }

      if (intendedLocaleRef.current === targetLocale && !userHasSelectedLocaleRef.current) {
        const seedLocale = resolveLazySeedLocale({ supportedLanguages });

        console.error(`Failed to load any locale; using CDS defaults for "${seedLocale}"`);
        seedCdsOnlyLocale(seedLocale);
        localStorage.setItem('language', seedLocale);
        _setUserLocale(seedLocale);
        bootstrapCompletedRef.current = true;
      }

      bootstrapCompletedRef.current = true;
    })();
  }, [ensureLocaleLoaded, initialUserLocale, isLazyMode, messagesLoaders, msgs, seedCdsOnlyLocale, supportedLanguages]);

  useEffect(() => {
    if (!isIframe && (!isLazyMode || userLocale)) {
      sendMessage({ language: activeLocale });
    }
  }, [activeLocale, isIframe, isLazyMode, sendMessage, userLocale]);

  const resetLanguageDefaults = useCallback(() => {
    const newSupportedLanguages = isLazyMode
      ? deriveLazySupportedLanguages({ messages: msgs, messagesLoaders, supportedLocales })
      : getDefaultLanguages();
    const resetLocale = isLazyMode
      ? resolveLazyFallbackLocale({
          supportedLanguages: newSupportedLanguages,
          messages: msgs,
          messagesLoaders,
        }) || DEFAULT_LANGUAGE
      : DEFAULT_LANGUAGE;

    setSupportedLanguages(newSupportedLanguages);
    setUserLocale(resetLocale);
  }, [isLazyMode, messagesLoaders, msgs, setUserLocale, supportedLocales]);

  return (
    <LocalizationContext.Provider
      value={{
        userLocale: userLocale || DEFAULT_LANGUAGE,
        setUserLocale,
        editMode,
        setEditMode,
        timeZone,
        setTimeZone,
        supportedLanguages,
        setSupportedLanguages,
        supportedDefaultLanguages: getDefaultLanguages(),
        resetLanguageDefaults,
        messages: mergedMessages,
        loadingLocale,
        parentAppMessages,
        setParentAppMessages,
      }}
    >
      <IntlProvider
        locale={activeLocale}
        messages={consolidatedMessages}
        onError={err => {
          if (err.code === 'MISSING_TRANSLATION') {
            const warnEnabled = localStorage.getItem('i18nWarnMissingTranslations') === 'true';
            if (warnEnabled) {
              console.warn('Missing translation', err.message);
            }
            return;
          }
          throw err;
        }}
      >
        {children}
      </IntlProvider>
    </LocalizationContext.Provider>
  );
};
